import React from 'react'
import PropTypes from 'prop-types'
import { Message } from '@paypalcorp/worldready-react'
import { BodyText } from '@paypalcorp/pp-react'
import { css } from '@emotion/core'
import ModalHeader from '../modal/ModalHeader'
import ModalContent from '../modal/ModalContent'
import ModalActions from '../modal/ModalActions'
import { ReactComponent as AlertIcon } from '../../icons/alert-3.svg'
import { ReactComponent as SuccessIcon } from '../../icons/success-3.svg'
import { ReactComponent as InfoIcon } from '../../icons/info-3.svg'
import { ReactComponent as WarningIcon } from '../../icons/warning-3.svg'
import { screenSmall } from 'components/utils/screen-sizes'
import { isMainView } from 'utils/server-data'
import { isOldFlowModal } from 'utils/flow'
import { modalPageCss } from 'components/styles'

const ICON_NAME_MAP = {
  alert: AlertIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  info: InfoIcon,
}

const iconPageCss = css`
  text-align: center;
  .papui_modal__header {
    margin: 1rem 0 0.75rem;
  }
  .papui_modal__content {
    margin-bottom: 3rem;
  }
  .page_icon {
    height: 9rem;
  }
  .body_text {
    margin-bottom: 2.5rem;
  }

  @media (max-width: ${screenSmall}) {
    .papui_modal__content {
      margin: 0;
    }
  }

  span:first-of-type {
    align-self: center;
  }

  // based on PPUI 4.0 guidelines, BodyText should be left aligned
  // if content has 3 lines or more
  .left_align {
    text-align: left;
  }

  ${modalPageCss}
`

const embeddedIconPageCss = css`
  padding: 2.5rem;
  text-align: center;
  .papui_modal__content {
    margin-bottom: 5rem;
  }
  .page_icon {
    height: 4rem;
  }
  #modal-header {
    margin-top: 0;
  }
  .body_text {
    margin-bottom: 1.5rem;
  }
  .papui_modal__actions {
    margin-top: 1.5rem;
  }
`

const mainIconPageCss = css`
  ${iconPageCss}

  .body_text {
    text-align: center;
  }
`

const vxModalContentCss = css`
  @media (max-width: ${screenSmall}) {
    padding-left: 1rem !important;
    padding-right 1rem !important;
    height: 100vh !important;
  }
`

function IconPage({
  embedded = false,
  iconName = '',
  headerProps = {},
  contentProps = {},
  headerId,
  headerTestId,
  contentId,
  contentTestId,
  moreContent,
  wrapWithModalContent = false,
  icon,
  leftAlignContent = false,
  contentCss,
  children,
  ...restProps
}) {
  const IconElement = ICON_NAME_MAP[iconName]
  let pageCss = iconPageCss

  if (embedded) {
    pageCss = embeddedIconPageCss
  } else if (isMainView()) {
    pageCss = mainIconPageCss
  }

  // To support common error pages for v1 flow after the removal of ModalBridge usage
  // to simplify implementation of common error pages shared between v1 and v2 flows
  // The class Name is to add additionaal margin/spacing to the content
  let className
  if (isOldFlowModal() && wrapWithModalContent) {
    className = 'vx_modal-content'
    pageCss = css`
      ${pageCss}
      ${vxModalContentCss}
    `
  }

  return (
    <div className={className} css={pageCss} {...restProps}>
      {icon ? icon : IconElement && <IconElement className="page_icon" />}
      {headerId && (
        <ModalHeader data-testid={headerTestId}>
          <Message id={headerId} {...headerProps} />
        </ModalHeader>
      )}
      <ModalContent css={contentCss}>
        {contentId && (
          <BodyText
            className={`body_text ${leftAlignContent ? 'left_align' : ''}`}
            data-testid={contentTestId}
          >
            <Message id={contentId} {...contentProps} />
          </BodyText>
        )}
        {moreContent}
        {children && <ModalActions> {children}</ModalActions>}
      </ModalContent>
    </div>
  )
}

IconPage.propTypes = {
  embedded: PropTypes.bool,
  iconName: PropTypes.string,
  headerId: PropTypes.string,
  contentId: PropTypes.string,
  headerTestId: PropTypes.string,
  contentTestId: PropTypes.string,
  headerProps: PropTypes.object,
  contentProps: PropTypes.object,
  moreContent: PropTypes.any,
  wrapWithModalContent: PropTypes.bool,
  icon: PropTypes.element,
  leftAlignContent: PropTypes.bool,
  contentCss: PropTypes.any,
  children: PropTypes.any,
}

export default IconPage
